<template>
  <div>
    <div class="tw:py-6 tw:lg:flex tw:gap-x-4 tw:hidden">
      <PropertiesFilter
        id="billing-properties-filter"
        ariaLabel="billing-properties-filter"
        size="big"
        class="tw:w-[14%] tw:min-w-[220px]"
        name="properties"
        :label="$t('hotels')"
        :options="propertiesOptions"
        :value="selectedPropertyValues"
        @select="onSelectedPropertyChange"
      />
      <StatusFilter
        id="billing-status-filter"
        ariaLabel="billing-status-filter"
        size="big"
        class="tw:w-[14%] tw:min-w-[220px]"
        name="status"
        :label="$t('tbs_invoice_status')"
        :options="statusOptions"
        :value="selectedStatusValues"
        @select="onSelectedStatusChange"
      />
    </div>
    <div class="tw:py-8 tw:hidden tw:md:flex tw:lg:hidden">
      <FlyoutFilters
        :currentFilters="currentFlyoutFilters"
        :propertiesOptions="propertiesOptions"
        :propertiesValue="selectedPropertyValues"
        :statusOptions="statusOptions"
        :statusValue="selectedStatusValues"
        @applyFilters="applyMobileFilters"
      />
    </div>
    <div class="tw:py-8 tw:flex tw:md:hidden">
      <GhostButton class="tw:flex tw:items-center tw:gap-2" @click="openMobileFilters">
        <FiltersIcon class="filter tw:fill-gray-800 tw:size-[18px]" />
        {{ $t('filters') }}
        <span
          v-if="selectedFiltersAmount > 0"
          class="filter-badge tw:bg-blue-700 tw:text-white tw:rounded-full tw:w-[20px] tw:h-[20px] tw:text-sm"
        >
          {{ selectedFiltersAmount }}
        </span>
      </GhostButton>

      <MobileFilters
        :propertiesOptions="propertiesOptions"
        :propertiesValue="selectedPropertyValues"
        :statusOptions="statusOptions"
        :statusValue="selectedStatusValues"
        @applyFilters="applyMobileFilters"
        @resetFilters="resetMobileFilters"
      />
    </div>
  </div>
</template>
<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'

import PropertiesFilter from '@/components/billing/premiumProducts/filters/PropertiesFilter.vue'
import StatusFilter from '@/components/billing/premiumProducts/filters/StatusFilter.vue'
import GhostButton from '@/components/base/buttons/GhostButton.vue'
import FiltersIcon from '@/components/icons/FiltersIcon.svg?component'
import MobileFilters from '@/components/billing/premiumProducts/MobileFilters.vue'
import FlyoutFilters from '@/components/billing/premiumProducts/FlyoutFilters.vue'

import { usePremiumProductsFilters } from '@/components/billing/premiumProducts/usePremiumProductsFilters'
import { storage } from '@/utils/storageService'

const props = defineProps({
  accommodationsData: Array,
})

const { openMobileFilters, selectedStatusValues, selectedPropertyValues, selectedFiltersAmount, closeMobileFilters } =
  usePremiumProductsFilters()

const currentFlyoutFilters = [
  { value: 'properties', label: 'hotels' },
  { value: 'status', label: 'tbs_invoice_status' },
]

const propertiesValues = computed(() => {
  return props.accommodationsData.map((item, index) => {
    return {
      id: index + 1,
      label: item?.name,
      value: item?.accommodationId,
    }
  })
})
const propertiesOptions = computed(() => {
  if (!Array.isArray(props.accommodationsData)) {
    return [{ value: 'all', label: 'campaign_subscription_status_all', id: 0 }]
  } else {
    return [{ value: 'all', label: 'campaign_subscription_status_all', id: 0 }, ...propertiesValues.value]
  }
})

const mainStatusOptions = [
  {
    id: 'all',
    label: 'campaign_subscription_status_all',
    value: 'all',
  },
  {
    group: { label: 'navigation_menu_business_studio_plus', value: 'plus', id: 'plus' },
    options: [
      { id: 'plus-active', label: 'subscriptions.status.active', value: 'active', product: 'plus' },
      { id: 'plus-expired', label: 'subscriptions.status.expired', value: 'expired', product: 'plus' },
      { id: 'plus-canceled', label: 'subscriptions.status.cancelled', value: 'cancelled', product: 'plus' },
    ],
  },
  {
    group: { label: 'navigation_menu_rate_connect', value: 'rate-connect', id: 'rate-connect' },
    options: [
      { id: 'rc-active', label: 'rc_provider_status_active', value: 'active', product: 'rc' },
      { id: 'rc-paused', label: 'rc_settings_campaign_off', value: 'paused', product: 'rc' },
      { id: 'rc-inactive', label: 'rc_provider_status_inactive', value: 'inactive', product: 'rc' },
    ],
  },
]

const proFilters = {
  group: { label: 'product.proPackage.name', value: 'pro', id: 'pro' },
  options: [
    { id: 'pro-active', label: 'subscriptions.status.active', value: 'active', product: 'pro' },
    { id: 'pro-cancelled', label: 'subscriptions.status.cancelled', value: 'cancelled', product: 'pro' },
    { id: 'pro-expired', label: 'subscriptions.status.expired', value: 'expired', product: 'pro' },
  ],
}

const hasPRO = ref(false)
let unsubscribeHasPRO = null

onMounted(() => {
  // Get initial values
  const hasPROSub = storage.getItem('hasProSubscription')
  if (hasPROSub) {
    hasPRO.value = JSON.parse(hasPROSub)
  }

  // Listen for changes
  unsubscribeHasPRO = storage.onChange('hasProSubscription', newValue => {
    hasPRO.value = newValue ? JSON.parse(newValue) : null
  })
})

onUnmounted(() => {
  unsubscribeHasPRO?.()
})

const statusOptions = computed(() => {
  if (hasPRO.value) {
    return [...mainStatusOptions, proFilters]
  } else {
    return mainStatusOptions
  }
})

const onSelectedStatusChange = newValue => {
  const isAllOption = newValue.id === 'all'
  const currentHasAll = selectedStatusValues.value.some(item => item.id === 'all')

  if (isAllOption) {
    selectedStatusValues.value = [newValue]
  } else {
    if (currentHasAll) {
      selectedStatusValues.value = selectedStatusValues.value.filter(item => item.id !== 'all')
    }

    const index = selectedStatusValues.value.findIndex(item => item.id === newValue.id)
    if (index > -1) {
      selectedStatusValues.value.splice(index, 1)
      if (selectedStatusValues.value.length === 0) {
        selectedStatusValues.value = [
          {
            value: 'all',
            label: 'campaign_subscription_status_all',
            id: 'all',
          },
        ]
      }
    } else {
      selectedStatusValues.value.push(newValue)
    }
  }
}

const onSelectedPropertyChange = newValue => {
  const isAllOption = newValue.id === 0
  const currentHasAll = selectedPropertyValues.value.some(item => item.id === 0)

  if (isAllOption) {
    selectedPropertyValues.value = [newValue]
  } else {
    if (currentHasAll) {
      selectedPropertyValues.value = selectedPropertyValues.value.filter(item => item.id !== 0)
    }

    const index = selectedPropertyValues.value.findIndex(item => item.id === newValue.id)
    if (index > -1) {
      selectedPropertyValues.value.splice(index, 1)

      if (selectedPropertyValues.value.length === 0) {
        selectedPropertyValues.value = [{ value: 'all', label: 'campaign_subscription_status_all', id: 0 }]
      }
    } else {
      selectedPropertyValues.value.push(newValue)
    }
  }
}

const countAppliedFilters = obj => {
  let filterCount = 0

  const propertyHasFilter = !(obj.property.length === 1 && obj.property[0].value === 'all')
  if (propertyHasFilter) filterCount++

  const statusHasFilter = !(obj.status.length === 1 && obj.status[0].value === 'all')
  if (statusHasFilter) filterCount++

  return filterCount
}

const applyMobileFilters = ({ property, status }) => {
  const filtersAmount = countAppliedFilters({ property, status })
  selectedPropertyValues.value = property
  selectedStatusValues.value = status
  selectedFiltersAmount.value = filtersAmount
  closeMobileFilters()
}

const resetMobileFilters = () => {
  selectedPropertyValues.value = [{ value: 'all', label: 'campaign_subscription_status_all', id: 0 }]
  selectedStatusValues.value = [{ value: 'all', label: 'campaign_subscription_status_all', id: 'all' }]
}
</script>
<style scoped>
button:hover .filter {
  stroke: var(--tw-color-white);
  fill: var(--tw-color-white);
}

button:hover .filter-badge {
  background-color: var(--tw-color-white);
  color: var(--tw-color-blue-700);
}
</style>
